import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Paper, Grid, Popover } from '@mui/material';
import { ArrowBack, List, Remove, CompareArrows, Print, Timeline } from '@mui/icons-material';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import PartnerFileStatesSelectionComponent from 'features/partnerFile/edit/states/components/PartnerFileStatesSelectionComponent';
import { emptyString } from 'constants/emptyConst';

const PartnerFileHeader = ({ documentId, siteId, fileNumber, changeIndex, setChangeIndex, isNewBulkFile, isNewPartnerFile }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);

  const openActions = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const inPartnerFile = history.location.pathname === `/partner-files/${documentId}/${siteId}`;

  const handleClose = () => {
    if (isNewBulkFile || isNewPartnerFile || inPartnerFile) {
      const urlFilters = sessionStorage.getItem('PartnerFileURLFilters');
      sessionStorage.removeItem('PartnerFileURLFilters');
      history.push('/partner-files' + (urlFilters ?? emptyString));
    } else {
      if (changeIndex !== 0) setChangeIndex(0);
      history.push(`/partner-files/${documentId}/${siteId}`);
    }
  };

  const onPrintClick = () => history.push(`/partner-files/${documentId}/${siteId}/documents`);
  const onStateTransitionsClick = () => history.push(`/partner-files/${documentId}/${siteId}/state-transitions`);
  const onWorkflowClick = () => history.push(`/partner-files/${documentId}/${siteId}/workflows`);

  const handleToggleActions = useCallback(event => {
    setAnchorEl(prev => {
      if (prev) return null;
      if (!event) return null;
      return event.currentTarget;
    });
  }, []);

  return (
    <Grid container alignItems='center'>
      <Grid container item xs={12} md={8} alignItems='center'>
        <Grid item mr={2}>
          <Typography fontWeight='bold' variant='subtitle1'>
            {fileNumber
              ? t('PartnerFile.PageTitle', { fileNumber })
              : isNewBulkFile
              ? t('PartnerFile.NewBulkFilePageTitle')
              : t('PartnerFile.NewPartnerFilePageTitle')}
          </Typography>
        </Grid>
        {!isNewPartnerFile && !isNewBulkFile && (
          <Grid item xs={12} lg={6}>
            <PartnerFileStatesSelectionComponent documentId={documentId} siteId={siteId} />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} md={4} display='flex' justifyContent='end'>
        <IconButton
          onClick={handleClose}
          color='primary'
          variant='text'
          tooltip={inPartnerFile ? t('PartnerFile.Actions.BackToList') : t('PartnerFile.Actions.BackToFile')}
        >
          <ArrowBack />
        </IconButton>
        {!isNewPartnerFile && !isNewBulkFile && (
          <>
            <IconButton onClick={onPrintClick} color='primary' variant='text' tooltip={t('OfferAction.Print')}>
              <Print />
            </IconButton>
            <IconButton
              color='primary'
              variant='text'
              onClick={handleToggleActions}
              tooltip={t(openActions ? 'Tooltips.HideHeaderActions' : 'Tooltips.ShowHeaderActions')}
            >
              {openActions ? <Remove /> : <List />}
            </IconButton>
          </>
        )}
      </Grid>
      {!isNewPartnerFile && !isNewBulkFile && (
        <Popover
          open={openActions}
          anchorEl={anchorEl}
          onClose={handleToggleActions}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Paper>
            <Grid container spacing={1} px={1} py={0.5}>
              <Grid item xs={6}>
                <IconButton
                  onClick={onStateTransitionsClick}
                  color='primary'
                  variant='text'
                  tooltip={t('PartnerFile.Actions.StateTransitions')}
                >
                  <CompareArrows />
                </IconButton>
              </Grid>
              <Grid item xs={6}>
                <IconButton onClick={onWorkflowClick} color='primary' variant='text' tooltip={t('OfferAction.Workflow')}>
                  <Timeline />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Popover>
      )}
    </Grid>
  );
};

PartnerFileHeader.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  fileNumber: PropTypes.string,
  changeIndex: PropTypes.number.isRequired,
  setChangeIndex: PropTypes.func.isRequired,
  isNewPartnerFile: PropTypes.bool,
  isNewBulkFile: PropTypes.bool
};

export default PartnerFileHeader;
